// Game state management and CSAT calculation

// Communication Matrix - CSAT values for all communication combinations
// Structure: tier -> channel -> timing -> tone -> CSAT value
// Values < -20 indicate protocol violations
export const COMMUNICATION_MATRIX = {
  VIP: {
    call: {
      proactive: { empathetic: 30, business: 20, minimal: 10 },
      reactive: { empathetic: 15, business: 10, minimal: 5 },
      delayed: { empathetic: -10, business: -15, minimal: -25 }
    },
    sms: {
      proactive: { empathetic: 10, business: 5, minimal: 0 },
      reactive: { empathetic: 0, business: -5, minimal: -15 },
      delayed: { empathetic: -15, business: -20, minimal: -30 }
    },
    email: {
      proactive: { empathetic: -25, business: -30, minimal: -35 },
      reactive: { empathetic: -35, business: -40, minimal: -45 },
      delayed: { empathetic: -45, business: -50, minimal: -55 }
    },
    push: {
      proactive: { empathetic: -20, business: -25, minimal: -30 },
      reactive: { empathetic: -30, business: -35, minimal: -40 },
      delayed: { empathetic: -40, business: -45, minimal: -50 }
    }
  },
  Business: {
    call: {
      proactive: { empathetic: 25, business: 20, minimal: 12 },
      reactive: { empathetic: 15, business: 12, minimal: 8 },
      delayed: { empathetic: 5, business: 0, minimal: -10 }
    },
    sms: {
      proactive: { empathetic: 20, business: 18, minimal: 12 },
      reactive: { empathetic: 12, business: 10, minimal: 5 },
      delayed: { empathetic: 0, business: -5, minimal: -15 }
    },
    email: {
      proactive: { empathetic: 10, business: 8, minimal: 5 },
      reactive: { empathetic: 5, business: 3, minimal: 0 },
      delayed: { empathetic: -10, business: -15, minimal: -20 }
    },
    push: {
      proactive: { empathetic: 18, business: 15, minimal: 10 },
      reactive: { empathetic: 10, business: 8, minimal: 5 },
      delayed: { empathetic: 0, business: -5, minimal: -15 }
    }
  },
  Standard: {
    call: {
      proactive: { empathetic: 15, business: 12, minimal: 8 },
      reactive: { empathetic: 10, business: 8, minimal: 5 },
      delayed: { empathetic: 5, business: 3, minimal: 0 }
    },
    sms: {
      proactive: { empathetic: 12, business: 10, minimal: 8 },
      reactive: { empathetic: 8, business: 7, minimal: 5 },
      delayed: { empathetic: 3, business: 0, minimal: -5 }
    },
    email: {
      proactive: { empathetic: 10, business: 10, minimal: 8 },
      reactive: { empathetic: 8, business: 8, minimal: 5 },
      delayed: { empathetic: 5, business: 3, minimal: 0 }
    },
    push: {
      proactive: { empathetic: 12, business: 12, minimal: 10 },
      reactive: { empathetic: 10, business: 10, minimal: 8 },
      delayed: { empathetic: 5, business: 5, minimal: 3 }
    }
  }
};

// Helper function to calculate minutes difference between two time strings (HH:MM format)
export const calculateMinutesDifference = (time1, time2) => {
  const parseTimeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  return parseTimeToMinutes(time2) - parseTimeToMinutes(time1);
};

// Calculate communication timing based on player's response speed
// timing: 'proactive' (≤5 min), 'reactive' (5-15 min), 'delayed' (>15 min)
// Now calculated from customer appearance time, not crisis start time
export const calculateCommunicationTiming = (customer, customerTracking, scenario) => {
  if (!customerTracking || !customerTracking.selected_at) {
    // Default to reactive if no tracking data
    return 'reactive';
  }

  // Use customer appearance time instead of crisis time
  const customerAppearsAt = customer?.appears_at || scenario.crisis_start_time || scenario.time?.start_time || '06:00';
  const communicationTime = customerTracking.selected_at;

  // Calculate delay in minutes from when customer appeared
  const delayMinutes = Math.abs(calculateMinutesDifference(customerAppearsAt, communicationTime));

  // Timing thresholds
  if (delayMinutes <= 5) {
    return 'proactive';
  } else if (delayMinutes <= 15) {
    return 'reactive';
  } else {
    return 'delayed';
  }
};

export const calculatePriority = (customer, currentTime, currentDay = 1) => {
  // Parse time strings (HH:MM) to minutes since midnight
  // Also handles formats like "Wednesday 14:00" by extracting just the time
  const parseTimeToMinutes = (timeString) => {
    // Extract time using regex to handle "Wednesday 14:00" or just "14:00"
    const timeMatch = timeString.match(/(\d{1,2}):(\d{2})/);
    if (!timeMatch) return 0;
    const hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    return hours * 60 + minutes;
  };

  const departMinutes = parseTimeToMinutes(customer.trip.original.departs);
  const currentMinutes = parseTimeToMinutes(currentTime);
  
  // For multi-day scenarios, use appears_at_day as approximation of departure day
  // If not specified, assume same day (day 1)
  const customerDay = customer.appears_at_day || 1;
  
  // Calculate total minutes from scenario start
  const currentTotalMinutes = (currentDay - 1) * 24 * 60 + currentMinutes;
  const departTotalMinutes = (customerDay - 1) * 24 * 60 + departMinutes;
  
  // Calculate minutes until departure
  let minutesUntilDeparture = departTotalMinutes - currentTotalMinutes;
  
  // If negative, departure has passed or is much later (next cycle)
  if (minutesUntilDeparture < 0) {
    // Assume departure is next occurrence (add 24 hours)
    minutesUntilDeparture += 24 * 60;
  }

  // Tier multipliers
  const tierMultipliers = {
    'VIP': 1.5,
    'Business': 1.2,
    'Standard': 1.0
  };

  // Calculate urgency level
  let urgencyLevel = 'STANDARD';
  let displayColor = 'normal';

  if (minutesUntilDeparture < 120) {
    urgencyLevel = 'CRITICAL';
    displayColor = 'critical';
  } else if (minutesUntilDeparture < 360 || customer.profile.tier === 'VIP') {
    urgencyLevel = 'HIGH';
    displayColor = 'high';
  }

  const score = (1000 - minutesUntilDeparture) * tierMultipliers[customer.profile.tier];

  return {
    score,
    urgencyLevel,
    minutesUntilDeparture: Math.max(0, Math.round(minutesUntilDeparture)),
    displayColor
  };
};

export const calculateCSAT = (decisions, customer, responseTime, customerTracking = null, scenario = null) => {
  let baseCsat = 50;

  // Get CSAT values from customer's options (if available) or use defaults
  // Transport CSAT comes from customer's options
  const transportEffect = decisions.transport?.csat || 0;

  // Communication CSAT from matrix with calculated timing
  const communicationEffect = getCommunicationCSAT(
    decisions.communication,
    customer.profile.tier,
    customerTracking,
    scenario,
    customer
  );

  // Accommodation and compensation CSAT from customer's options
  const accommodationEffect = decisions.accommodation?.csat || 0;
  const compensationEffect = decisions.compensation?.csat || 0;

  const totalEffect = transportEffect + communicationEffect + accommodationEffect + compensationEffect;

  // Apply tier multiplier
  const tierMultipliers = {
    'VIP': 1.5,
    'Business': 1.0,
    'Standard': 0.8
  };

  // Apply time penalty if response was slow
  const slaSeconds = {
    'VIP': 300,      // 5 minutes
    'Business': 600,  // 10 minutes
    'Standard': 900   // 15 minutes
  };

  const timeFactor = responseTime <= slaSeconds[customer.profile.tier] ? 1.0 : 0.7;

  const finalCsat = Math.max(0, Math.min(100,
    (baseCsat + totalEffect) * tierMultipliers[customer.profile.tier] * timeFactor
  ));

  return Math.round(finalCsat);
};

export const getCommunicationCSAT = (communication, tier, customerTracking = null, scenario = null) => {
  if (!communication) return -15; // Penalty for no communication

  // Calculate timing based on player's response speed
  const timing = calculateCommunicationTiming(
    null, // customer not needed for timing calculation
    customerTracking,
    scenario
  );

  // Lookup CSAT from matrix: tier -> channel -> timing -> tone
  const channel = communication.details?.channel;
  const tone = communication.details?.tone;

  if (!channel || !tone) {
    return 0; // Fallback if details missing
  }

  return COMMUNICATION_MATRIX[tier]?.[channel]?.[timing]?.[tone] || 0;
};

export const calculateTotalCost = (decisions) => {
  let total = 0;
  if (decisions.transport) total += decisions.transport.cost;
  // Communication has no cost
  if (decisions.accommodation) total += decisions.accommodation.cost;
  if (decisions.compensation) total += decisions.compensation.cost;
  return total;
};

export const getCSATEmoji = (csat) => {
  if (csat >= 85) return '😊';
  if (csat >= 70) return '🙂';
  if (csat >= 50) return '😐';
  if (csat >= 30) return '😟';
  return '😠';
};

export const getCSATColor = (csat) => {
  if (csat >= 85) return 'hsl(var(--normal))';
  if (csat >= 70) return 'hsl(var(--info))';
  if (csat >= 50) return 'hsl(var(--high))';
  return 'hsl(var(--critical))';
};

export const formatTime = (timeString) => {
  return timeString;
};

export const parseTime = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

export const formatCurrency = (amount) => {
  return `€${amount.toLocaleString()}`;
};

export const calculateGameTime = (realSeconds, timeScale) => {
  return realSeconds * timeScale;
};

export const formatGameTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const formatGameTimeWithDay = (seconds) => {
  const totalMinutes = Math.floor(seconds / 60);
  const day = Math.floor(totalMinutes / (24 * 60)) + 1;
  const minutesInDay = totalMinutes % (24 * 60);
  const hours = Math.floor(minutesInDay / 60);
  const minutes = minutesInDay % 60;
  
  // Map day number to weekday (Day 1 = Monday, Day 2 = Tuesday, etc.)
  const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const weekdayIndex = (day - 1) % 7;
  const weekday = weekdays[weekdayIndex];
  
  return `${weekday}, ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};